
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { AlertTriangle, ArrowRight, Building, Cloud, Laptop, CheckCircle2 } from "lucide-react";

const Solutions = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Section */}
        <section className="py-10 md:py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-4xl mx-auto">
              <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold mb-4 md:mb-6">
                <span className="text-gray-800">Tailored </span>
                <span className="text-[#8932c8]">Solutions</span>
                <span className="text-gray-800"> for Every Security Challenge</span>
              </h1>
              <p className="text-base md:text-lg lg:text-xl text-gray-600 mb-8 md:mb-12">
                Our Agentic AI-powered XDR platform adapts to the unique security needs of organizations of all sizes, from enterprises to government agencies.
              </p>
            </div>
          </div>
        </section>

        {/* Solutions Description */}
        <section className="py-8 md:py-12 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="text-center max-w-4xl mx-auto mb-8 md:mb-16">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6">
                <span className="text-gray-800">Our </span>
                <span className="text-[#8932c8]">Solutions</span>
              </h2>
              <p className="text-base md:text-lg text-gray-600">
                Discover how Spharaka's revolutionary Agentic AI platform addresses the specific cybersecurity challenges faced by different types of organizations.
              </p>
            </div>
          </div>
        </section>

        {/* Enterprise Solution */}
        <section className="py-10 md:py-16 bg-white">
          <div className="container mx-auto px-4 md:px-6">
            <div className="bg-[#8932c8] text-white p-6 md:p-8 lg:p-12 rounded-xl mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Spharaka for Enterprise</h2>
              <p className="text-base md:text-xl">Comprehensive protection for complex security environments</p>
            </div>

            <div className="mb-10 md:mb-16">
              <p className="text-base md:text-lg text-gray-700 mb-8 md:mb-12">
                Spharaka for Enterprise delivers unified security across your global infrastructure, cloud environments, and diverse workforces. Our Agentic AI platform adapts to your organization's unique security posture, providing autonomous protection while integrating with your existing security investments.
              </p>

              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center">Enterprise Challenges & Spharaka Solutions</h3>

              <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-10 md:mb-16">
                {/* Challenge 1 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Expanding Attack Surface</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Managing security across hybrid cloud environments, remote workforces, and thousands of interconnected devices creates visibility gaps.
                  </p>
                </div>

                {/* Solution 1 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Unified Security Operations</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Consolidates visibility and control across endpoints, networks, cloud, and applications, providing a complete view of your security posture regardless of where your assets are located.
                  </p>
                </div>

                {/* Challenge 2 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Advanced Persistent Threats</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Sophisticated attackers specifically target enterprises with long-term campaigns designed to evade traditional security measures.
                  </p>
                </div>

                {/* Solution 2 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Autonomous Threat Hunting</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Proactively identifies sophisticated attack patterns that bypass traditional detection methods, with continuous AI-driven hunting that adapts to evolving threat techniques.
                  </p>
                </div>
              </div>

              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center">Additional Enterprise Capabilities</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Executive-level Reporting</p>
                    <p className="text-xs md:text-sm text-gray-600">translates security metrics into business risk insights for leadership and board communication.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Enterprise-wide Zero Trust</p>
                    <p className="text-xs md:text-sm text-gray-600">implementation automates continuous verification across users, devices, and applications.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Advanced Behavioral Analytics</p>
                    <p className="text-xs md:text-sm text-gray-600">detects insider threats and account compromise through AI-powered anomaly detection.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Enterprise Security Roadmap</p>
                    <p className="text-xs md:text-sm text-gray-600">support with dedicated security advisors to guide platform optimization.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SMB Solution */}
        <section className="py-10 md:py-16 bg-slate-50">
          <div className="container mx-auto px-4 md:px-6">
            <div className="bg-[#8932c8] text-white p-6 md:p-8 lg:p-12 rounded-xl mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Spharaka for SMB</h2>
              <p className="text-base md:text-xl">Flexible, scalable protection for organizations of all sizes - from small businesses to large enterprises and government agencies</p>
            </div>

            <div className="mb-10 md:mb-16">
              <p className="text-base md:text-lg text-gray-700 mb-8 md:mb-12">
                Spharaka for SMB makes advanced cybersecurity accessible to organizations with limited security resources. Our solution delivers comprehensive protection with minimal configuration and management overhead, allowing you to focus on your business while our platform handles the security.
              </p>

              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center">SMB Challenges & Spharaka Solutions</h3>

              <div className="grid md:grid-cols-2 gap-4 md:gap-8 mb-10 md:mb-16">
                {/* Challenge 1 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Limited Security Expertise</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Most SMBs lack dedicated security personnel, leaving IT generalists to manage cybersecurity alongside other responsibilities.
                  </p>
                </div>

                {/* Solution 1 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Autonomous Security Operations</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Minimizes the need for security expertise, with AI handling detection and response. Clear guidance and recommendations help IT staff make informed security decisions.
                  </p>
                </div>

                {/* Challenge 2 */}
                <div className="border border-orange-200 bg-orange-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-orange-100 rounded-full text-orange-600">
                      <AlertTriangle size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Growing Target Profile</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Cyber attackers increasingly target SMBs due to their typically weaker security postures and valuable business and client data.
                  </p>
                </div>

                {/* Solution 2 */}
                <div className="border border-violet-200 bg-blue-50 p-4 md:p-6 rounded-lg">
                  <div className="flex items-start gap-3 mb-3 md:mb-4">
                    <span className="p-2 bg-violet-100 rounded-full text-[#8932c8]">
                      <CheckCircle2 size={18} />
                    </span>
                    <h4 className="font-semibold text-lg md:text-xl">Enterprise-Grade Protection</h4>
                  </div>
                  <p className="text-gray-700 text-sm md:text-base">
                    Delivers advanced threat detection and response capabilities with flexible deployment options for enterprises, SMBs, and government organizations.
                  </p>
                </div>
              </div>

              <h3 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-center">Additional SMB Capabilities</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Rapid Deployment</p>
                    <p className="text-xs md:text-sm text-gray-600">allows for complete protection in hours with pre-configured security policies and minimal setup.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Simplified Management Console</p>
                    <p className="text-xs md:text-sm text-gray-600">provides clear visibility and guidance with minimal security jargon.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">Automated Compliance Reporting</p>
                    <p className="text-xs md:text-sm text-gray-600">simplifies meeting industry requirements and customer security questionnaires.</p>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <span className="text-[#8932c8] mt-1">
                    <CheckCircle2 size={16} />
                  </span>
                  <div>
                    <p className="font-semibold text-sm md:text-base">24/7 Security Monitoring</p>
                    <p className="text-xs md:text-sm text-gray-600">provides round-the-clock protection without requiring staff overtime.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Solutions;
